import {useState} from 'react'
import {AlertIcon, StopIcon} from '@primer/octicons-react'
import {Flash} from '@primer/react'
import {ConflictingFilesList} from '@github-ui/mergebox/components/sections/conflicts/ConflictingFilesList'
import {ResolveConflictsButton} from '@github-ui/mergebox/components/sections/conflicts/ResolveConflictsButton'
import {
  CONFLICT_SECTION_STATUSES,
  ConflictResolutionSubtitle,
} from '@github-ui/mergebox/components/sections/conflicts/conflicts-section-presentation'
import {ForYouItem} from '../shared/ForYouItem'
import {useValidatedConflictsData} from './use-validated-conflicts-data'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'

// Renders when the branch has merge conflicts that must be resolved
export function MergeConflicts() {
  const data = useValidatedConflictsData()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  if (!data) {
    return null
  }

  const {pullRequest, conflictsSection, canUserPushToBase} = data
  const {shouldRender, sectionStatus, conflictsCondition} = conflictsSection
  const isConflictState = sectionStatus === 'HAS_CONFLICTS' || sectionStatus === 'HAS_REBASE_CONFLICTS'

  if (!shouldRender || !isConflictState || !conflictsCondition) {
    return null
  }

  const sectionPresentation = CONFLICT_SECTION_STATUSES[sectionStatus]
  const conflictsWebEditorPath = `${pullRequest.resourcePath}/conflicts`
  const showAgentOption = canUserPushToBase && !!pullRequest.viewerCanUseCopilotCodingAgent

  // only list files if user can resolve conflict or user can push to head (aka has sufficient access)
  const shouldListConflictingFiles =
    sectionStatus === 'HAS_CONFLICTS' &&
    (conflictsCondition.webEditorConflictResolution?.viewerCanResolve ||
      conflictsCondition.webEditorConflictResolution?.viewerCannotResolve?.reason !== 'INSUFFICIENT_ACCESS')

  const conflicts = conflictsCondition.conflicts ?? []

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={AlertIcon} variant="danger" />}
      title={sectionPresentation.heading}
      itemName="merge-conflicts"
      padding="none"
      description={
        sectionStatus === 'HAS_CONFLICTS' ? (
          <ConflictResolutionSubtitle
            conflictsWebEditorPath={conflictsWebEditorPath}
            webEditorConflictResolution={conflictsCondition.webEditorConflictResolution}
          />
        ) : undefined
      }
      actionSlot={
        <ResolveConflictsButton
          conflictsWebEditorPath={conflictsWebEditorPath}
          conflictsState={sectionStatus}
          onError={setErrorMessage}
          showAgentOption={showAgentOption}
          webEditorConflictResolution={conflictsCondition.webEditorConflictResolution}
          size="small"
        />
      }
    >
      {shouldListConflictingFiles && <ConflictingFilesList conflicts={conflicts} />}
      {errorMessage && (
        <Flash className="tmp-m-3" variant="danger" role="alert">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
    </ForYouItem>
  )
}
